// frontend/src/components/grid/ForecastTooltip.jsx
// Custom AG Grid tooltip for forecast cells.
// Shows color-coded math breakdown: System → User → Promotion → Final

import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../hooks/useFormatters';
import './TooltipCard.css';

const ForecastTooltip = (props) => {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const data = props.data;
  const monthKey = props.colDef?.field;
  if (!data || !monthKey) return null;

  const fmt = (v) => formatNumber(v, 0);

  const stat = data[`${monthKey}_statistical`];
  if (stat === null || stat === undefined) return null;

  const consensus = data[`${monthKey}_consensus`];
  const promotion = data[`${monthKey}_promotion`];
  const statRounded = Math.round(stat);

  // The flag the cell's user ink reads (utils/forecastRows.js): a merchant who
  // pinned the month to the engine's own figure still made an adjustment.
  const hasUserOverride = data[`${monthKey}_user_modified`];

  // Determine if promotion is active
  const hasPromotion = promotion !== null && promotion !== undefined;

  // The two deltas the breakdown adds up. The promotion delta is the one the
  // server stores — the number the promotion engine pinned — never inferred
  // from a total that has already absorbed it.
  const userDelta = Math.round(consensus) - statRounded;
  const promoDelta = hasPromotion ? Math.round(promotion) : 0;

  // Final value — the same sum the grid cell and the backend show, kept as an
  // addition here so the breakdown provably totals to the figure beneath it:
  // system + userDelta + promoDelta === finalValue, by construction. The user
  // line is shown only when flagged; an unflagged month's delta is 0 because
  // its consensus IS the engine's number — the forecast run writes it so
  // (`_save_forecast_outputs_batch`) and a clear restores it
  // (`forecasting.consensus.write_final_units`).
  //
  // Derived from the PERSISTED consensus rather than read off `params.value`,
  // which carries an in-flight edit: a tooltip whose Final disagreed with the
  // three lines above it would be worse than one that lags the cell by a save.
  //
  // `consensus` is non-null whenever this renders — it is NOT NULL on the
  // model, so a row that exists has one, and the Final block only renders when
  // there is an override or a promotion, both of which require a row.
  const finalValue = hasPromotion ? consensus + promotion : consensus;

  // Simple case: no user override, no promotion — just show system value
  const isSimple = !hasUserOverride && !hasPromotion;

  return (
    <div className="tooltip-card">
      {/* System forecast — always shown */}
      <div className="tooltip-card-row tooltip-card-system">
        <span className="tooltip-card-label">{t('forecast.manexysForecast')}</span>
        <span className="tooltip-card-value">{fmt(stat)}</span>
      </div>

      {/* User adjustment — only when modified */}
      {hasUserOverride && (
        <div className="tooltip-card-row tooltip-card-user">
          <span className="tooltip-card-label">{t('forecast.yourAdjustment')}</span>
          <span className="tooltip-card-value">
            {userDelta >= 0 ? '+' : ''}{userDelta}
          </span>
        </div>
      )}

      {/* Promotion adjustment — only when active */}
      {hasPromotion && (
        <div className="tooltip-card-row tooltip-card-promo">
          <span className="tooltip-card-label">{t('forecast.promotionAdjustment')}</span>
          <span className="tooltip-card-value">
            {promoDelta >= 0 ? '+' : ''}{promoDelta}
          </span>
        </div>
      )}

      {/* Separator + Final — only when there are adjustments */}
      {!isSimple && (
        <>
          <div className="tooltip-card-divider" />
          <div className="tooltip-card-row tooltip-card-final">
            <span className="tooltip-card-label">{t('forecast.finalForecast')}</span>
            <span className="tooltip-card-value">{fmt(finalValue)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default ForecastTooltip;
